import axios from 'axios';

const api = axios.create({ baseURL: '/api' });

// Produktiv authentisiert das oauth2-proxy-Cookie (HttpOnly) und
// ForwardAuth injiziert den Bearer auf /api/*; die SPA haelt dann gar kein
// Token. Nur der Dev-Fallback (`?token=`, AuthContext) setzt eines -- und das
// liegt nur im Speicher, nie in Web Storage (docs/app-integration/
// app-integration-oidc.md, "Tokens im Browser: Regeln"). Nach dem Neuladen
// ist es weg.
let devToken: string | null = null;
export function setDevToken(token: string | null): void {
  devToken = token;
}
/** Dev-Bearer aktiv? EventSource kann ihn nicht mitschicken (Polling-Fallback). */
export function hasDevToken(): boolean {
  return devToken !== null;
}
// Altlast: frueher lag der Dev-Token im localStorage.
try {
  localStorage.removeItem('platform-admin.token');
} catch {
  /* Storage gesperrt -- dann liegt dort auch nichts */
}

api.interceptors.request.use((config) => {
  const token = devToken;
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  (r) => r,
  (err) => {
    // Don't auto-reload on the identity probe — AuthContext handles a failed
    // /me by showing the sign-in screen. Reloading there would loop.
    const url: string = err.config?.url ?? '';
    if (err.response?.status === 401 && !url.endsWith('/me')) {
      devToken = null;
      window.location.reload();
    }
    return Promise.reject(err);
  },
);

export default api;

export interface TenantSummary {
  id: string;
  slug: string;
  name: string;
  adminEmail: string;
  plan: string;
  status: string;
  realmName: string;
  hostingProvider: string;
  hostingRegion: string | null;
  hostingTarget: string | null;
  selectedServices: string[];
  createdAt: string;
  updatedAt: string;
}

export interface Provider {
  id: string;
  name: string;
  description: string;
  regions: string[];
  custom: boolean;
}

export interface CatalogApp {
  slug: string;
  name: string;
  description: string;
  category: string;
  status: string;
  isBase: boolean;
  deploymentTargets: string[];
}

export interface TenantDetail extends TenantSummary {
  /** Testzugang (Selbstregistrierung): Ende der Testphase, null = kein Testzugang. */
  trialEndsAt?: string | null;
  trialStage?: string | null;
  userCount: number | null;
  // White-Label-Marke + optionaler MSP-Kontext des Mandanten (Spalten der
  // Tenant-Zeile, vom Detail-Endpoint mitgeliefert). Zugewiesen über
  // PUT /api/tenants/:slug/brand bzw. /context.
  brand: string;
  contextId: string | null;
  // Customer (Kunde) the deployment belongs to. customerId is the scalar FK;
  // customer carries the label fields. Assigned via PUT /tenants/:slug/customer.
  customerId: string | null;
  customer: { id: string; companyName: string; displayName: string | null } | null;
  planHistory: Array<{ id: string; fromPlan: string | null; toPlan: string; changedBy: string; reason: string | null; createdAt: string }>;
  awxJobs: Array<{ id: string; template: string; status: string; startedAt: string | null; finishedAt: string | null }>;
  ctiDeployments: Array<{ id: string; vmTemplate: string; profiles: string[]; status: string; host: string | null; startedAt: string | null; finishedAt: string | null }>;
}

export interface AuditEntry {
  id: string;
  tenantId: string | null;
  actorSub: string;
  actorEmail: string;
  action: string;
  payload: unknown;
  createdAt: string;
}

// Welle 4.5 — Enterprise federation registry. Backend model
// is the Prisma EnterpriseTarget row introduced in PR #1044.
// `lastStatus` values are drawn from the FederatedFetchResult
// status union — see federated-fetch.service for the source
// of truth.
export type FederationStatus =
  | 'unknown'
  | 'reachable'
  | 'unreachable'
  | 'auth_failed'
  | 'timeout'
  | 'disabled'
  // `local` = a managed tenant with no federated scoped platform-admin.
  // Its marketplace data comes from the authoritative central record
  // (Tenant.selectedServices), so it is "known" (counts toward coverage)
  // even though there is no remote instance to reach. Used by the
  // Marketplace-Fleet aggregator so the whole fleet renders, not just
  // Enterprise-federation targets.
  | 'local';

export interface EnterpriseTarget {
  slug: string;
  baseUrl: string;
  hmacSecretOpenBaoPath: string;
  lastStatus: FederationStatus;
  lastReachableAt: string | null;
  enabled: boolean;
  createdAt: string;
  updatedAt: string;
}

// Welle 4.6 H-6 — federation auth-log surfacing. Mirrors the
// shape returned by `GET /api/federation/auth-log` (central
// aggregator) and `GET /api/federated/auth-log` (scoped read).
export interface FederationAuthEvent {
  id: string;
  at: string;
  cn: string;
  result: 'accepted' | 'rejected' | string;
  reason: string | null;
  path: string;
  method: string;
  /**
   * Slug of the scoped instance the event came from. Populated
   * server-side by the central aggregator when merging across
   * tenants; absent on a direct scoped-side read.
   */
  slug?: string;
}

export interface FederationAuthLogTenantCard {
  slug: string;
  status: FederationStatus;
  durationMs: number;
  error?: string;
  allowedCNs: string[];
  uniqueCNs: string[];
  accepted: number;
  rejected: number;
  /** CNs that authenticated but are NOT in the scoped allowlist. */
  unknownCNs: string[];
  events: FederationAuthEvent[];
}

export interface FederationAuthLogResponse {
  events: FederationAuthEvent[];
  tenants: FederationAuthLogTenantCard[];
  federation: Record<string, { status: FederationStatus; durationMs: number; error?: string }>;
}

// -----------------------------------------------------------------
// Marketplace fleet view (Welle 4.6 NS-3 MVP).
//
// Backed by `GET /api/marketplace/fleet` (central) which fans
// out `GET /api/federated/marketplace/installed` to every enabled
// EnterpriseTarget and joins against the central catalog. The
// SPA renders the per-app/per-tenant matrix as a table on the
// /marketplace-fleet page.
//
// Cell rendering rules:
//   - `installedOn` includes a tenant → installed (green)
//   - `notInstalledOn` includes a tenant → missing (amber)
//   - tenant is in `tenants` but in NEITHER list → unknown (slate)
//     (the scoped instance was unreachable; see `federation` map)
//
// Coverage classification (mirrors the backend math):
//   - "full"    → installedOn covers every reachable tenant
//   - "partial" → installedOn has at least one but not all reachable
//   - "none"    → installedOn is empty
//
// "reachable tenant count" is derived in the SPA from the
// `federation` map rather than transmitted as a separate field —
// that way a future status string addition (e.g. "degraded")
// doesn't require a backend change to keep the SPA's filter menu
// correct.
// -----------------------------------------------------------------

export interface MarketplaceFleetApp {
  slug: string;
  name: string;
  category: string;
  /** Tenant slugs (reachable) that have this app installed. */
  installedOn: string[];
  /** Tenant slugs (reachable) that do NOT have this app installed. */
  notInstalledOn: string[];
}

export interface MarketplaceFleetResponse {
  apps: MarketplaceFleetApp[];
  tenants: string[];
  federation: Record<string, { status: FederationStatus; durationMs: number; error?: string }>;
}

// -----------------------------------------------------------------
// Service-Versionen — operator version-drift views.
//
// Tab 1 (marketplace): catalog-latest SemVer vs. the version really
// installed per tenant. Tab 2 (platform): declared/expected version
// vs. the LIVE running version on central + per tenant.
//
// Drift status drives the badge colour; `notInCatalog` is an
// app-level state (installed somewhere but absent from the catalog).
// -----------------------------------------------------------------

export type DriftStatus = 'current' | 'outdated' | 'ahead' | 'unknown' | 'notInCatalog';

export type ProbeStatus = 'ok' | 'unreachable' | 'no-version-endpoint' | 'third-party';

// How a platform service's live version was obtained:
//   'http'  → HTTP GET /api/version (our own services)
//   'image' → container image tag via Prometheus (third-party infra)
//   'none'  → not probed (third-party placeholder)
export type ProbeSource = 'http' | 'image' | 'none';

export interface ServiceVersionInstallation {
  tenantSlug: string;
  installedSemver: string | null;
  drift: DriftStatus;
}

export interface ServiceVersionApp {
  slug: string;
  name: string;
  category: string;
  catalogLatestSemver: string | null;
  inCatalog: boolean;
  installations: ServiceVersionInstallation[];
  driftCounts: Record<DriftStatus, number>;
}

export interface ServiceVersionsMarketplaceResponse {
  apps: ServiceVersionApp[];
  tenants: string[];
  federation: Record<string, { status: FederationStatus; durationMs: number; error?: string }>;
}

export interface PlatformServiceTenantCell {
  tenantSlug: string;
  liveVersion: string | null;
  status: ProbeStatus;
  source: ProbeSource;
  drift: DriftStatus;
}

export interface PlatformServiceRow {
  id: string;
  name: string;
  expectedVersion: string | null;
  thirdParty: boolean;
  central: {
    liveVersion: string | null;
    commit: string | null;
    status: ProbeStatus;
    source: ProbeSource;
    drift: DriftStatus;
  };
  tenants: PlatformServiceTenantCell[];
  driftCounts: Record<DriftStatus, number>;
}

export interface ServiceVersionsPlatformResponse {
  services: PlatformServiceRow[];
  tenants: string[];
  federation: Record<string, { status: FederationStatus; durationMs: number; error?: string }>;
}

// Mirrors `AwxJobListRow` in src/routes/awx-jobs.routes.ts. Surfaced
// by GET /api/awx-jobs across all tenants — operators land here on
// the /jobs SPA page to triage tenant-lifecycle AWX work
// (provisioning, suspend, plan-change, decommission) without
// drilling into each tenant detail page.
export interface AwxJobRow {
  id: string;
  tenantId: string;
  tenantSlug: string;
  tenantName: string;
  template: string;
  status: string;
  startedAt: string | null;
  finishedAt: string | null;
  payload: unknown;
  result: unknown;
}

export interface AwxJobStats {
  total: number;
  pending: number;
  failed: number;
}

export interface AwxJobListResponse {
  total: number;
  rows: AwxJobRow[];
  stats: AwxJobStats;
}

// SSE frame published on `/api/awx-jobs/stream` for the `awx.job`
// event. The route also emits `hello` (on connect) and `heartbeat`
// frames — those don't carry job data, the consumer just keeps the
// connection open.
export interface AwxJobEvent {
  type: 'awx.job.updated';
  jobId: string;
  tenantId: string;
  tenantSlug: string;
  tenantName: string;
  template: string;
  status: string;
  startedAt: string | null;
  finishedAt: string | null;
}

// -----------------------------------------------------------------
// CTI-stack deployments.
//
// Backed by GET /api/cti-deployments (+ /stream SSE, POST launch). A CTI
// deployment provisions a nested-KVM IONOS VM and deploys the
// SecTepe.Core.CTI docker/CAPE/sandbox stack onto it. Unlike AwxJob this is
// platform infrastructure — `tenantSlug` is optional (null = platform-global).
// Mirrors CtiDeploymentListRow / CtiDeployEvent on the backend.
// -----------------------------------------------------------------

export const CTI_PROFILES = ['basic', 'cape', 'sandbox', 'misp', 'opencti'] as const;
export const CTI_VM_TEMPLATES = ['CAPE_S', 'CAPE_M', 'CAPE_L', 'CAPE_XL'] as const;
export type CtiProfile = (typeof CTI_PROFILES)[number];
export type CtiVmTemplate = (typeof CTI_VM_TEMPLATES)[number];

export interface CtiDeploymentRow {
  id: string;
  tenantId: string | null;
  tenantSlug: string | null;
  tenantName: string | null;
  target: string;
  host: string | null;
  vmTemplate: string;
  profiles: string[];
  status: string;
  startedAt: string | null;
  finishedAt: string | null;
  payload: unknown;
  result: unknown;
  createdBy: string;
  createdAt: string;
}

export interface CtiDeploymentStats {
  total: number;
  pending: number;
  failed: number;
}

export interface CtiDeploymentListResponse {
  total: number;
  rows: CtiDeploymentRow[];
  stats: CtiDeploymentStats;
}

// SSE frame published on /api/cti-deployments/stream for the `cti.deploy`
// event. The route also emits `hello` + `heartbeat` frames (no payload).
export interface CtiDeployEvent {
  type: 'cti.deploy.updated';
  id: string;
  tenantId: string | null;
  target: string;
  host: string | null;
  vmTemplate: string;
  profiles: string[];
  status: string;
  startedAt: string | null;
  finishedAt: string | null;
}

export interface CreateCtiDeploymentInput {
  vmTemplate: CtiVmTemplate;
  profiles: CtiProfile[];
  tenantSlug?: string;
  region?: string;
  domain?: string;
}

export async function createCtiDeployment(
  input: CreateCtiDeploymentInput,
): Promise<{ id: string; jobId: string | number; pollUrl: string }> {
  const { data } = await api.post('/cti-deployments', input);
  return data;
}

// Welle 4.6 NS-2 — per-tenant branding for the tenant-admin portal
// SPA. The operator surface (Branding tab on TenantDetailPage)
// reads/writes via these two endpoints; the tenant-admin portal
// reads its own row via `/api/me/tenant/branding`.
//
// Server validation is authoritative (see
// platform-admin/src/services/branding.service.ts) — the SPA
// re-checks shape only to give immediate feedback in the form.
export interface TenantBranding {
  tenantSlug: string;
  displayName: string;
  primaryColor: string;
  logoUrl: string | null;
  supportEmail: string | null;
  updatedBy: string;
  updatedAt: string;
  // True when the response is a synthesised default (no
  // persisted DB row). The SPA renders a "noch nicht
  // angepasst" hint based on this flag.
  isDefault: boolean;
}

// -----------------------------------------------------------------
// Plan-change-request queue (Welle 4.6 — QoL-3).
//
// Backed by `GET /api/tenants/plan-change-requests` (federation-
// merged across local + Enterprise scoped instances) and the new
// `POST /api/tenants/:slug/plan-change-request/:auditId/reject`
// endpoint. The `PlanChangeRequestRow` shape mirrors the backend
// service so the SPA can render directly off the response.
//
// `federationSource` is `"local"` for central-Postgres rows and the
// scoped tenant slug otherwise — drives the green/blue badge on
// PlanChangeRequestsPage.
// -----------------------------------------------------------------

export type PlanChangeRequestStatus = 'pending' | 'resolved';
export type PlanChangeResolutionKind = 'plan-change' | 'rejected';

export interface PlanChangeRequestRow {
  id: string;
  auditId: string;
  tenantId: string;
  tenantSlug: string;
  tenantName: string;
  fromPlan: string;
  requestedPlan: string;
  currentPlan: string;
  reason: string;
  requestedBySub: string;
  requestedByEmail: string;
  requestedAt: string;
  status: PlanChangeRequestStatus;
  resolvedAt: string | null;
  resolvedToPlan: string | null;
  resolutionKind: PlanChangeResolutionKind | null;
  rejectionReason: string | null;
  federationSource: string;
}

export interface PlanChangeRequestListResponse {
  total: number;
  rows: PlanChangeRequestRow[];
  /**
   * Per-scoped-slug federation call meta. Only present on the
   * central instance's response; scoped instances answer with
   * just `{ total, rows }`. The shape mirrors the existing
   * tenant-list federation envelope.
   */
  federation?: Record<string, { status: string; durationMs: number; error?: string }>;
}

/**
 * Approve a pending plan-change request by kicking the existing
 * plan-change path. The backend rejects this if the tenant is
 * already on the target plan (409) — the SPA should disable the
 * Approve button in that case but the server-side guard remains
 * the source of truth.
 *
 * The optional operator note is appended to the existing
 * `reason` field that the plan-change endpoint already audits;
 * we don't introduce a separate field because plan changes
 * outside this queue (operator-initiated, no PCR) use the same
 * reason channel.
 */
export async function approvePlanChangeRequest(
  slug: string,
  requestedPlan: 'basic' | 'business' | 'enterprise',
  reason: string,
): Promise<{ jobId: string | number }> {
  const { data } = await api.put<{ jobId: string | number }>(`/tenants/${slug}/plan`, {
    plan: requestedPlan,
    reason,
  });
  return data;
}

/**
 * Reject a pending plan-change request. Writes a
 * `plan.change.rejected` audit row referencing the originating
 * request; the next list read sees the request as resolved.
 *
 * `rejectionReason` is required (validated server-side as a
 * non-empty string up to 500 chars).
 */
export async function rejectPlanChangeRequest(
  slug: string,
  auditId: string,
  rejectionReason: string,
): Promise<{ id: string; originalAuditId: string }> {
  const { data } = await api.post<{ id: string; originalAuditId: string }>(
    `/tenants/${slug}/plan-change-request/${auditId}/reject`,
    { rejectionReason },
  );
  return data;
}

// -----------------------------------------------------------------
// Bulk operations helper (Welle 4.6 — QoL-1).
//
// The platform-admin SPA exposes single-row lifecycle actions
// (suspend, reactivate, plan-change, federation enable/disable/
// remove). Operators occasionally need to fan these out over many
// tenants in one go — partner-region maintenance windows, batch
// price-tier migrations, federation cleanup after a rollout.
//
// Backend endpoints are reused as-is: each row makes its own call.
// This keeps the audit log natural (one TenantAudit per row, with
// the operator's actorSub baked in by the OIDC middleware), and
// avoids inventing new bulk endpoints with their own validation
// surface.
//
// Caveat: AWX templates fan out under suspend / reactivate / plan-
// change, so we cap the parallelism well below the row count to
// avoid hammering the AWX worker pool. The default of 5 matches
// the per-operator AWX template concurrency budget used elsewhere
// in the central plane (heartbeat / federated polling). Callers
// can lower it further for especially destructive batches.
// -----------------------------------------------------------------

export type BulkStatus = 'queued' | 'in-flight' | 'success' | 'error';

export interface BulkResult<TResult> {
  slug: string;
  status: BulkStatus;
  result?: TResult;
  error?: string;
}

export interface BulkOperationOptions<TResult> {
  /** Max concurrent operations. Defaults to 5. */
  concurrency?: number;
  /**
   * Called whenever a row transitions state (queued -> in-flight ->
   * success/error). Lets the caller render per-row progress without
   * having to wait for the whole batch to finish.
   */
  onUpdate?: (entry: BulkResult<TResult>, index: number) => void;
}

/**
 * Extract a human-readable error message out of the axios error
 * envelope used everywhere in this SPA. The {@link api} client
 * surfaces the backend's `{ error: string }` body when present and
 * falls back to the JS `Error.message` for network failures.
 */
export function extractApiError(err: unknown): string {
  const e = err as { response?: { data?: { error?: string } }; message?: string };
  return e.response?.data?.error ?? e.message ?? 'Unbekannter Fehler';
}

/**
 * Generate an opaque bulk-operation correlation ID. Sent on each
 * per-row request as the `X-Bulk-Operation-Id` header so the
 * server-side audit middleware can group the rows back together
 * later (e.g. "show me every action that was part of bulk run X").
 *
 * Uses crypto.randomUUID() when available (modern browsers + Node
 * 19+), falls back to a timestamp-prefixed random string. Either
 * way it's only used for correlation, never trusted by the
 * backend for authorization.
 */
export function newBulkOperationId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `bulk-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * Run an async operation against every slug in `slugs` with a
 * bounded concurrency, capturing per-slug success / error so the
 * caller can render row-level progress and (later) retry the
 * failing subset without re-running the successful rows.
 *
 * Never throws — a thrown `op` becomes a captured `error` entry.
 * The returned promise resolves once every row has settled.
 */
export async function bulkOperation<TResult>(
  slugs: string[],
  op: (slug: string) => Promise<TResult>,
  opts: BulkOperationOptions<TResult> = {},
): Promise<BulkResult<TResult>[]> {
  const concurrency = Math.max(1, opts.concurrency ?? 5);
  const results: BulkResult<TResult>[] = slugs.map((slug) => ({ slug, status: 'queued' }));

  let cursor = 0;
  const advance = async (): Promise<void> => {
    while (cursor < slugs.length) {
      const idx = cursor;
      cursor += 1;
      const slug = slugs[idx];
      results[idx] = { slug, status: 'in-flight' };
      opts.onUpdate?.(results[idx], idx);
      try {
        const result = await op(slug);
        results[idx] = { slug, status: 'success', result };
      } catch (err) {
        results[idx] = { slug, status: 'error', error: extractApiError(err) };
      }
      opts.onUpdate?.(results[idx], idx);
    }
  };

  // Spin up at most `concurrency` workers — each pulls from the
  // shared cursor until the slug list is drained. Promise.all
  // resolves when every worker is done, which means every row has
  // settled into success or error.
  const workers = Array.from({ length: Math.min(concurrency, slugs.length) }, () => advance());
  await Promise.all(workers);
  return results;
}

// Welle 4.6 TD-6 — per-target federation health history. Mirrors
// the shape returned by `GET /api/enterprise-targets/:slug/history`.
// Each event corresponds to one state-changing recordCallResult
// write (status-change dedup is enforced server-side, so the
// timeline never carries duplicate consecutive entries).
export interface EnterpriseTargetHistoryEvent {
  id: string;
  /** ISO-8601 UTC timestamp of the recorded call. */
  at: string;
  status: FederationStatus;
  /**
   * Per-call duration in milliseconds. Null when the entry is an
   * `auth_failed` from an unresolvable OpenBao secret (no fetch
   * was attempted, so there is no duration to record).
   */
  durationMs: number | null;
  /**
   * Per-call error string. Absent on `reachable` / `disabled` and
   * on entries where the upstream did not surface a message.
   */
  error?: string;
}

export interface EnterpriseTargetHistoryResponse {
  rows: EnterpriseTargetHistoryEvent[];
  /**
   * Total count of matching rows in the queried window — useful
   * for the "showing 200 of N" hint when the server-side cap
   * truncates the response. May exceed `rows.length`.
   */
  total: number;
}

/**
 * Aggregate stats over a list of history events. Used by the
 * SPA modal's stats card so the rendering side doesn't have to
 * duplicate the reduction logic across the JSX.
 */
export interface EnterpriseTargetHistoryStats {
  total: number;
  reachableCount: number;
  uptimeRatio: number | null;
  avgDurationMs: number | null;
  breakerOpenCount: number;
}

export function computeHistoryStats(
  events: EnterpriseTargetHistoryEvent[],
): EnterpriseTargetHistoryStats {
  const total = events.length;
  let reachableCount = 0;
  let breakerOpenCount = 0;
  let durationSum = 0;
  let durationCount = 0;
  for (const ev of events) {
    if (ev.status === 'reachable') reachableCount += 1;
    if (ev.status === 'unreachable') breakerOpenCount += 1;
    if (typeof ev.durationMs === 'number') {
      durationSum += ev.durationMs;
      durationCount += 1;
    }
  }
  return {
    total,
    reachableCount,
    uptimeRatio: total === 0 ? null : reachableCount / total,
    avgDurationMs: durationCount === 0 ? null : durationSum / durationCount,
    breakerOpenCount,
  };
}

// -----------------------------------------------------------------
// Service-Pakete (Achse B) + Bestellungen.
//
// Katalog + per-Mandant-Zuweisung (Operator) sowie die Bestell-Queue
// von der Homepage. Backend: src/routes/service-package.routes.ts,
// tenant.routes.ts (PUT/GET :slug/service-package), order.routes.ts.
// -----------------------------------------------------------------

export interface ServiceModule {
  id: string;
  name: string;
  description: string;
  category: string;
  services: string[];
}

export interface ServiceBundle {
  id: string;
  name: string;
  description: string;
  modules: string[];
}

export interface ServicePackageCatalog {
  core: string[];
  modules: ServiceModule[];
  bundles: ServiceBundle[];
}

export interface TenantServicePackage {
  slug: string;
  plan: string;
  status: string;
  services: string[];
  activeModules: string[];
  catalog: ServicePackageCatalog;
}

export interface ApplyPackageResult {
  slug: string;
  services: string[];
  added: string[];
  removed: string[];
  activeModules: string[];
}

export async function getServicePackageCatalog(): Promise<ServicePackageCatalog> {
  const { data } = await api.get<ServicePackageCatalog>('/service-packages');
  return data;
}

export async function getTenantServicePackage(slug: string): Promise<TenantServicePackage> {
  const { data } = await api.get<TenantServicePackage>(`/tenants/${slug}/service-package`);
  return data;
}

export async function applyTenantServicePackage(
  slug: string,
  selection: { modules: string[]; bundles: string[] },
): Promise<ApplyPackageResult> {
  const { data } = await api.put<ApplyPackageResult>(`/tenants/${slug}/service-package`, selection);
  return data;
}

/**
 * Per-plan entitlements surfaced by `GET /api/plans` (plan.routes.ts).
 * `quota` + `backup` mirror tenant-plans.yaml; `usage` + `isolation`
 * are derived (quota.service.ts / tenant.service.ts). Used to show the
 * operator what a plan provisions at selection/approval time.
 */
export interface PlanEntitlement {
  name: 'basic' | 'business' | 'enterprise';
  description: string;
  quota: {
    requests_cpu: string;
    requests_memory: string;
    limits_cpu: string;
    limits_memory: string;
    pvc_count: number;
    storage: string;
    services_loadbalancer: number;
    services_nodeport: number;
  };
  backup: { velero_ttl: string; drill_schedule: string };
  usage: {
    /** null = unlimited (enterprise). */
    active_users: number | null;
    mail_storage_bytes: number | null;
    index_storage_bytes: number | null;
    mail_msgs: number | null;
    search_queries: number | null;
    enforced: boolean;
  };
  isolation: {
    mode: 'isolated' | 'dedicated-lite';
    dedicatedRealm: boolean;
    dedicatedNodePool: boolean;
    vclusterReplicas: number;
  };
  /** Listenpreise aus der aktiven Preisliste; null wenn keine aktiv ist. */
  pricing: {
    baseCents: number;
    /** Seat-Preis Jahresvertrag. */
    seatCents: number;
    minSeats: number | null;
    /** Seat-Preis bei monatlicher Kündbarkeit; null = nicht angeboten. */
    seatMonthlyCents: number | null;
    /** Mengenstaffel, aufsteigend; gilt für alle Seats ab der Schwelle. */
    volumeTiers: Array<{ fromSeats: number; seatCents: number }>;
    /** Hosting je Seat (Cloud); null = im Seat-Preis enthalten, 0 = Preis offen. */
    hostingSeatCents: number | null;
    priceListName: string;
  } | null;
}

export async function getPlans(): Promise<PlanEntitlement[]> {
  const { data } = await api.get<{ plans: PlanEntitlement[] }>('/plans');
  return data.plans;
}

// ----- VDI / Linux Terminal Server (marketplace service) -------------------
// Per-tenant Apache Guacamole + Linux desktops. Provisioning is AWX-mediated;
// these call the platform-admin endpoints in tenant.routes.ts.
export interface VdiConfig {
  enabled: boolean;
  minPool: number;
  maxDesktops: number;
  idleTimeoutMin: number;
  /** Guacamole gateway ("Klassisch (RDP)"); false = browser streaming only. */
  guacamole: boolean;
  lastJobId: number | null;
  updatedAt: string | null;
}

export interface VdiConfigInput {
  minPool?: number;
  maxDesktops?: number;
  idleTimeoutMin?: number;
  guacamole?: boolean;
}

export async function getTenantVdi(slug: string): Promise<VdiConfig> {
  const { data } = await api.get<VdiConfig>(`/tenants/${slug}/vdi`);
  return data;
}

export async function enableTenantVdi(
  slug: string,
  input: VdiConfigInput,
): Promise<{ jobId: number; config: VdiConfig }> {
  const { data } = await api.post<{ jobId: number; config: VdiConfig }>(`/tenants/${slug}/vdi/enable`, input);
  return data;
}

export async function updateTenantVdi(
  slug: string,
  input: VdiConfigInput,
): Promise<{ jobId: number; config: VdiConfig }> {
  const { data } = await api.put<{ jobId: number; config: VdiConfig }>(`/tenants/${slug}/vdi`, input);
  return data;
}

export async function disableTenantVdi(
  slug: string,
  purge = false,
): Promise<{ jobId: number }> {
  const { data } = await api.post<{ jobId: number }>(`/tenants/${slug}/vdi/disable`, { purge });
  return data;
}

/** Desktop profile: one more Windows desktop of the tenant (docs/vdi/desktop-profile.md). */
export interface VdiProfile {
  slot: number;
  key: string;
  label: string;
  group: string;
  maxDesktops: number;
  minPool: number;
  kind?: 'linux' | 'windows';
  updatedAt?: string;
}

export async function getTenantVdiProfiles(slug: string): Promise<VdiProfile[]> {
  const { data } = await api.get<{ profiles: VdiProfile[] }>(`/tenants/${slug}/vdi/profiles`);
  return data.profiles;
}

/** Replaces the set of ONE kind and starts the VDI playbook of that kind (202 + jobId). */
export async function saveTenantVdiProfiles(
  slug: string,
  kind: 'linux' | 'windows',
  profiles: VdiProfile[],
): Promise<{ jobId: number; profiles: VdiProfile[] }> {
  const body = profiles.map(({ slot, key, label, group, maxDesktops, minPool }) => ({ slot, key, label, group, maxDesktops, minPool }));
  const { data } = await api.put<{ jobId: number; profiles: VdiProfile[] }>(`/tenants/${slug}/vdi/profiles`, { kind, profiles: body });
  return data;
}

// ----- Webhosting (ADR 0015) -----------------------------------------------
// Per-tenant Plesk-like webhosting: freischalten/abschalten via AWX,
// Obergrenzen, Lesesicht auf die Websites. Backend: tenant-webhosting.routes.ts.
export interface WebhostingConfig {
  enabled: boolean;
  maxSites: number;
  maxDomains: number;
  maxDatabases: number;
  maxDnsZones: number;
  webspaceGi: number;
  phpVersions: string[];
  // Phase 2
  maxCronJobs: number;
  maxMailboxes: number;
  nodeVersions: string[];
  // Phase 3: Traffic je Monat in GiB (null = unbegrenzt), Verhalten bei Ueberschreitung
  trafficGiPerMonth: number | null;
  trafficPolicy: 'notify' | 'suspend' | string;
  lastJobId: number | null;
  updatedAt: string | null;
  filesHost: string;
  siteDomainSuffix: string;
  nameservers: string[];
}

export interface WebhostingLimitsInput {
  maxSites?: number;
  maxDomains?: number;
  maxDatabases?: number;
  maxDnsZones?: number;
  webspaceGi?: number;
  phpVersions?: string[];
  maxCronJobs?: number;
  maxMailboxes?: number;
  nodeVersions?: string[];
  trafficGiPerMonth?: number | null;
  trafficPolicy?: 'notify' | 'suspend';
}

export interface WebhostingUsage {
  sites: number;
  domains: number;
  databases: number;
  dnsZones: number;
  fileAccounts: number;
  storageBytes: number | null;
  cronJobs: number;
  mailboxes: number;
  mailDomains: number;
  apps?: number;
  trafficBytesMonth?: number;
}

export interface WebhostingTrafficState {
  month: string;
  usedBytes: number;
  limitBytes: number | null;
  policy: string;
  exceeded: boolean;
  overridden: boolean;
  suspendedSites: number;
}

export interface WebhostingStackStatus {
  ready: boolean;
  webspace: { ready: boolean; usedBytes: number | null; sizeBytes: number | null };
  mariadb: { ready: boolean };
  sftpgo: { ready: boolean; webUrl: string | null; webdavUrl: string | null };
}

export interface WebhostingOverview {
  config: WebhostingConfig;
  usage: WebhostingUsage;
  stack: WebhostingStackStatus | null;
  controllerConfigured: boolean;
}

export interface WebhostingSiteRow {
  id: string;
  name: string;
  runtime: string;
  phpVersion: string | null;
  status: string;
  statusMessage: string | null;
  suspendedReason?: string | null;
  primaryUrl: string | null;
  domains: Array<{ fqdn: string; kind: string; certStatus: string }>;
  createdAt: string;
}

export async function getTenantWebhosting(slug: string): Promise<WebhostingOverview> {
  const { data } = await api.get<WebhostingOverview>(`/tenants/${slug}/webhosting`);
  return data;
}

export async function enableTenantWebhosting(
  slug: string,
  input: WebhostingLimitsInput,
): Promise<{ jobId: number; config: WebhostingConfig }> {
  const { data } = await api.post<{ jobId: number; config: WebhostingConfig }>(`/tenants/${slug}/webhosting/enable`, input);
  return data;
}

export async function updateTenantWebhosting(slug: string, input: WebhostingLimitsInput): Promise<WebhostingConfig> {
  const { data } = await api.put<WebhostingConfig>(`/tenants/${slug}/webhosting`, input);
  return data;
}

export async function disableTenantWebhosting(slug: string, purge = false): Promise<{ jobId: number }> {
  const { data } = await api.post<{ jobId: number }>(`/tenants/${slug}/webhosting/disable`, { purge });
  return data;
}

export async function resyncTenantWebhosting(slug: string): Promise<{ applied: number; failed: number }> {
  const { data } = await api.post<{ applied: number; failed: number }>(`/tenants/${slug}/webhosting/resync`);
  return data;
}

export async function getTenantWebhostingTraffic(slug: string): Promise<WebhostingTrafficState> {
  const { data } = await api.get<WebhostingTrafficState>(`/tenants/${slug}/webhosting/traffic`);
  return data;
}

export async function liftTenantWebhostingTraffic(slug: string): Promise<WebhostingTrafficState> {
  const { data } = await api.post<WebhostingTrafficState>(`/tenants/${slug}/webhosting/traffic/lift`);
  return data;
}

export async function listTenantWebhostingSites(slug: string): Promise<WebhostingSiteRow[]> {
  const { data } = await api.get<{ items: WebhostingSiteRow[] }>(`/tenants/${slug}/webhosting/sites`);
  return data.items;
}

export interface OrderRow {
  id: string;
  companyName: string;
  contactEmail: string;
  requestedSlug: string;
  plan: string;
  modules: string[];
  bundles: string[];
  note: string | null;
  status: 'unverified' | 'pending' | 'approved' | 'rejected';
  source: string;
  /** 'trial' = Testzugang aus der Selbstregistrierung (docs/tenant/trial-self-service.md). */
  kind?: 'order' | 'trial';
  emailVerifiedAt?: string | null;
  decidedBy: string | null;
  decisionNote: string | null;
  tenantSlug: string | null;
  createdAt: string;
  decidedAt: string | null;
}

export interface OrderListResponse {
  total: number;
  rows: OrderRow[];
}

export async function listOrders(status?: string, kind?: 'order' | 'trial'): Promise<OrderListResponse> {
  const { data } = await api.get<OrderListResponse>('/orders', {
    params: { status: status ?? 'pending', limit: 200, ...(kind ? { kind } : {}) },
  });
  return data;
}

export interface TrialCapacity { active: number; max: number; available: boolean }

export async function getTrialCapacity(): Promise<TrialCapacity> {
  const { data } = await api.get<TrialCapacity>('/orders/trial-capacity');
  return data;
}

export async function extendTrial(slug: string, days: number): Promise<{ slug: string; trialEndsAt: string }> {
  const { data } = await api.post<{ slug: string; trialEndsAt: string }>(`/tenants/${slug}/trial/extend`, { days });
  return data;
}

export async function convertTrial(slug: string): Promise<{ slug: string; converted: boolean }> {
  const { data } = await api.post<{ slug: string; converted: boolean }>(`/tenants/${slug}/trial/convert`, {});
  return data;
}

export async function approveOrder(
  id: string,
  opts: { hostingProvider?: string; hostingRegion?: string } = {},
): Promise<{ tenantSlug: string; jobId: string | number }> {
  const { data } = await api.post<{ tenantSlug: string; jobId: string | number }>(
    `/orders/${id}/approve`,
    opts,
  );
  return data;
}

export async function rejectOrder(id: string, reason: string): Promise<OrderRow> {
  const { data } = await api.post<OrderRow>(`/orders/${id}/reject`, { reason });
  return data;
}

// === App-Marketplace Self-Service (Anfrage → Freigabe → Deploy) ===

export type AppRequestStatus =
  | 'pending' | 'approved' | 'rejected' | 'cancelled' | 'deploying' | 'deployed' | 'failed';

export interface AppRequestRow {
  id: string;
  tenantSlug: string;
  appSlug: string;
  appName: string;
  category: string | null;
  deploymentTarget: string;
  note: string | null;
  requestedBySub: string;
  requestedByEmail: string;
  source: string; // tenant-portal | webui
  status: AppRequestStatus;
  decidedBy: string | null;
  decisionNote: string | null;
  installationId: string | null;
  deployJobId: string | null;
  failureReason: string | null;
  createdAt: string;
  decidedAt: string | null;
  deployedAt: string | null;
}

export interface AppRequestListResponse {
  total: number;
  rows: AppRequestRow[];
}

export async function listAppRequests(status?: string, tenantSlug?: string): Promise<AppRequestListResponse> {
  const params: Record<string, string | number> = { status: status ?? 'pending', limit: 200 };
  if (tenantSlug && tenantSlug.trim()) params.tenantSlug = tenantSlug.trim();
  const { data } = await api.get<AppRequestListResponse>('/app-requests', { params });
  return data;
}

/**
 * Approve = install. `deploymentTarget` lets the operator override the
 * target resolved at request time (validated against the catalog server-side).
 */
export async function approveAppRequest(
  id: string,
  deploymentTarget?: 'kubernetes' | 'docker',
): Promise<AppRequestRow> {
  const { data } = await api.post<AppRequestRow>(
    `/app-requests/${id}/approve`,
    deploymentTarget ? { deploymentTarget } : {},
  );
  return data;
}

export async function rejectAppRequest(id: string, reason: string): Promise<AppRequestRow> {
  const { data } = await api.post<AppRequestRow>(`/app-requests/${id}/reject`, { reason });
  return data;
}

/** Read-through App-Marketplace catalog (operator-facing browse view). */
export async function listAppCatalog(): Promise<CatalogApp[]> {
  const { data } = await api.get<{ apps: CatalogApp[] }>('/catalog');
  return data.apps;
}

// One-time retrieval of a tenant's initial admin credentials. The backend
// reads them from a read-once OpenBao path and destroys it, so this returns
// a usable password EXACTLY once; a second call rejects with HTTP 410. The
// caller must surface the result immediately and never persist it.
export interface InitialAdminCredentials {
  username: string | null;
  password: string | null;
  accountUrl: string | null;
}

export async function getInitialAdminCredentials(slug: string): Promise<InitialAdminCredentials> {
  const { data } = await api.get<InitialAdminCredentials>(`/tenants/${slug}/admin-credentials`);
  return data;
}

// Re-issue a tenant's initial admin credentials (customer lost them before
// first login). Launches the AWX reset job; once it finishes, the operator
// reveals the fresh credentials again via getInitialAdminCredentials.
export async function resetInitialAdminCredentials(slug: string): Promise<{ jobId: string | number }> {
  const { data } = await api.post<{ jobId: string | number }>(`/tenants/${slug}/admin-credentials/reset`, {});
  return data;
}

// Erst-Admin-Adresse nachziehen (z. B. nach einer Umbenennung im Realm).
// Server prueft: Mandanten-Maildomain + bestehendes Mitglied von
// /tenant-admin. Der Wert speist den Credentials-Reset.
export async function updateTenantAdminEmail(
  slug: string, adminEmail: string,
): Promise<{ adminEmail: string; changed: boolean }> {
  const { data } = await api.put<{ adminEmail: string; changed: boolean }>(`/tenants/${slug}/admin-email`, { adminEmail });
  return data;
}

// Attach a customer-owned (BYO) domain to a tenant. Launches the AWX
// add-domain job (cert via ACME HTTP-01 + Traefik route + Nextcloud
// trusted_domains). The customer must already point the domain at the
// cluster ingress, else the ACME challenge stays pending.
export async function attachCustomDomain(slug: string, domain: string): Promise<{ jobId: string | number }> {
  const { data } = await api.post<{ jobId: string | number }>(`/tenants/${slug}/domains`, { domain });
  return data;
}

// The DNS records an operator must create (today manually in Core-Networks)
// so a tenant's product hostnames resolve. Mirrors what provisioning will
// auto-create once DNS automation is enabled.
export interface TenantDnsRecord {
  host: string;
  fqdn: string;
  type: string;
  data: string;
  ttl: number;
}
export interface TenantDnsRecords {
  zone: string;
  target: string;
  records: TenantDnsRecord[];
}
export async function getTenantDnsRecords(slug: string): Promise<TenantDnsRecords> {
  const { data } = await api.get<TenantDnsRecords>(`/tenants/${slug}/dns-records`);
  return data;
}

// =================================================================
// MSP-Verwaltung: Verträge, Lizenzen, MSP-Kontexte, Marken.
//
// Die DTOs spiegeln 1:1 die View-Typen der Backend-Services:
//   - ContextView  → provisioning-context.service.ts (/api/contexts)
//   - BrandView    → brand.service.ts                 (/api/brands)
//   - ContractView → contract.service.ts              (/api/tenants/:slug/contract)
//   - LicenseView  → license.service.ts               (/api/tenants/:slug/license)
// Bei Drift ist der Server autoritativ; diese Typen dienen nur dem
// SPA-Typecheck + der Render-Schicht.
// =================================================================

// MSP-Kontext (Provisioning-Context, N3-4) — benannte, brand-skopierte
// Gruppierung, unter der Mandanten provisioniert werden.
export interface ContextView {
  id: string;
  name: string;
  brand: string;
  externalRef: string | null;
  status: string; // active | archived
  tenantCount: number;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export async function listContexts(): Promise<ContextView[]> {
  const { data } = await api.get<ContextView[]>('/contexts');
  return data;
}

// Customer (Kunde) — commercial/billing master-data entity. A Tenant is ONE
// DEPLOYMENT; one customer owns multiple deployments. The customer-level
// "Vertragsmanagement" view aggregates the per-tenant contracts/licenses.
export interface CustomerView {
  id: string;
  companyName: string;
  displayName: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  postalCode: string | null;
  city: string | null;
  country: string | null;
  vatId: string | null;
  billingEmail: string | null;
  billingContact: string | null;
  externalRef: string | null;
  brand: string;
  status: string; // active | archived
  tenantCount: number;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface CustomerDeployment {
  slug: string;
  name: string;
  plan: string;
  status: string;
}

export interface CustomerContractsAggregate {
  customerId: string;
  deploymentCount: number;
  contractsProvisioned: number;
  licensesProvisioned: number;
  totals: {
    storageQuotaBytes: number;
    aiTokenQuota: number;
    aiTokensUsed: number;
    bucketCount: number;
    seats: number;
    usedSeats: number;
  };
  deployments: Array<{
    slug: string;
    name: string;
    plan: string;
    status: string;
    contract: {
      provisioned: boolean;
      status: string;
      storageQuotaBytes: number;
      aiTokenQuota: number;
      aiTokensUsed: number;
      bucketCount: number;
    } | null;
    license: {
      provisioned: boolean;
      status: string;
      seats: number;
      usedSeats: number;
    } | null;
  }>;
}

// Payload for create/update — companyName required on create; all else optional.
export type CustomerInput = Partial<Omit<CustomerView, 'id' | 'tenantCount' | 'createdBy' | 'createdAt' | 'updatedAt' | 'status'>> & {
  companyName?: string;
  status?: string;
};

export async function listCustomers(): Promise<CustomerView[]> {
  const { data } = await api.get<CustomerView[]>('/customers');
  return data;
}

export async function getCustomer(id: string): Promise<CustomerView> {
  const { data } = await api.get<CustomerView>(`/customers/${id}`);
  return data;
}

export async function createCustomer(input: CustomerInput): Promise<CustomerView> {
  const { data } = await api.post<CustomerView>('/customers', input);
  return data;
}

export async function updateCustomer(id: string, input: CustomerInput): Promise<CustomerView> {
  const { data } = await api.patch<CustomerView>(`/customers/${id}`, input);
  return data;
}

export async function deleteCustomer(id: string): Promise<void> {
  await api.delete(`/customers/${id}`);
}

export async function listCustomerDeployments(id: string): Promise<CustomerDeployment[]> {
  const { data } = await api.get<CustomerDeployment[]>(`/customers/${id}/deployments`);
  return data;
}

export async function getCustomerContracts(id: string): Promise<CustomerContractsAggregate> {
  const { data } = await api.get<CustomerContractsAggregate>(`/customers/${id}/contracts`);
  return data;
}

/** Assign a deployment (tenant) to a customer, or clear it with null. */
export async function assignTenantCustomer(slug: string, customerId: string | null): Promise<void> {
  await api.put(`/tenants/${slug}/customer`, { customerId });
}

// Marke (Multi-Brand / White-Label, N3-5) — Theming-Defaults, die neue
// Mandanten unter dieser Marke erben. Die White-Label-Extras (Produktname,
// Logos, Rechtslinks, Mail) sind nullable; `null`/'' = "erben" (Deployment-
// Default bzw. Haus-Marke). Spiegel von brand.service.ts#BrandView.
export interface BrandExtras {
  productName: string | null;
  /** ≤ 12 Zeichen (App-Switcher / PWA short_name). */
  shortName: string | null;
  /** ≤ 160 Zeichen. */
  tagline: string | null;
  logoUrlDark: string | null;
  faviconUrl: string | null;
  accentColor: string | null;
  supportUrl: string | null;
  docsUrl: string | null;
  imprintUrl: string | null;
  privacyUrl: string | null;
  termsUrl: string | null;
  websiteUrl: string | null;
  emailFromName: string | null;
  /** ≤ 300 Zeichen, Klartext-Fusszeile transaktionaler Mails. */
  emailFooter: string | null;
}

export const BRAND_EXTRA_KEYS: ReadonlyArray<keyof BrandExtras> = [
  'productName', 'shortName', 'tagline', 'logoUrlDark', 'faviconUrl', 'accentColor',
  'supportUrl', 'docsUrl', 'imprintUrl', 'privacyUrl', 'termsUrl', 'websiteUrl',
  'emailFromName', 'emailFooter',
];

export interface BrandView extends BrandExtras {
  slug: string;
  displayName: string;
  primaryColor: string;
  logoUrl: string | null;
  supportEmail: string | null;
  status: string; // active | archived
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

/** POST /api/brands — Extras optional (`undefined` = nicht gesetzt). */
export interface BrandCreateInput extends Partial<Record<keyof BrandExtras, string | null>> {
  slug: string;
  displayName: string;
  primaryColor: string;
  logoUrl?: string;
  supportEmail?: string;
}

/** PATCH /api/brands/:slug — `null` loescht ein Feld, `undefined` laesst es unveraendert. */
export interface BrandUpdateInput extends Partial<Record<keyof BrandExtras, string | null>> {
  displayName?: string;
  primaryColor?: string;
  logoUrl?: string | null;
  supportEmail?: string | null;
  status?: 'active' | 'archived';
}

export async function listBrands(): Promise<BrandView[]> {
  const { data } = await api.get<BrandView[]>('/brands');
  return data;
}

export async function createBrand(input: BrandCreateInput): Promise<BrandView> {
  const { data } = await api.post<BrandView>('/brands', input);
  return data;
}

export async function updateBrand(slug: string, input: BrandUpdateInput): Promise<BrandView> {
  const { data } = await api.patch<BrandView>(`/brands/${encodeURIComponent(slug)}`, input);
  return data;
}

// GET /api/brands-meta — welcher Slug der Deployment-Default (BRAND_SLUG) ist,
// d. h. was `/api/public/brand` ohne Tenant ausliefert, und welcher Slug die
// (geschuetzte) Haus-Marke ist.
export interface BrandsMeta {
  defaultBrandSlug: string;
  houseBrandSlug: string;
}

export async function getBrandsMeta(): Promise<BrandsMeta> {
  const { data } = await api.get<BrandsMeta>('/brands-meta');
  return data;
}

// Vertrag (Contract-Manager, N2-3) — per-Mandant S3-Buckets, AI-Token- und
// Storage-Quota. Der Server synthetisiert beim ersten Read einen
// `provisioned:false`-Default, falls noch keine Vertragszeile existiert.
export interface BucketView {
  id: string;
  name: string;
  region: string;
  purpose: string;
  status: string;
  createdAt: string;
}

export interface ContractView {
  tenantSlug: string;
  provisioned: boolean;
  s3ContractRef: string | null;
  status: string; // active | suspended
  storageQuotaBytes: number;
  aiTokenQuota: number;
  aiTokensUsed: number;
  aiTokensRemaining: number | null;
  buckets: BucketView[];
  createdBy: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

// Lizenz (Seat-Lizenz, N2-2) — per-Mandant User-Seat-Limit + Zählung.
// `seats === 0` bedeutet unbegrenzt; `status === 'none'` = keine Lizenz
// gesetzt (Seat-Checks behandeln das als unbegrenzt).
export interface LicenseView {
  tenantSlug: string;
  provisioned: boolean;
  extRef: string | null;
  brand: string | null;
  status: string; // none | active | suspended
  seats: number;
  usedSeats: number;
  seatsRemaining: number | null;
  createdBy: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

// ===========================================================================
// Abrechnung (Billing) — /api/billing (billing.routes.ts). Konzept:
// docs/business/billing-concept.md. Geldbeträge sind Integer-Cent.
// ===========================================================================

export type BillingSide = 'revenue' | 'cost';
export type LineSource = 'plan' | 'seat' | 'module' | 'usage' | 'manual' | 'model' | 'opencost';

export interface BillingLine {
  side: BillingSide;
  category: string;
  code: string;
  description: string;
  quantity: number;
  unit: string;
  unitPriceCents: number;
  amountCents: number;
  metric: string | null;
  source: LineSource;
}

export interface UsageDetail {
  code: string;
  metric: string;
  label: string;
  used: number;
  included: number | null;
  overage: number;
  unit: string;
  billedCents: number;
}

export interface TenantBillingConfig {
  tenantSlug: string;
  provisioned: boolean;
  billable: boolean;
  committedSeats: number | null;
  discountBp: number;
  flatPriceCents: number | null;
  billingTerm: BillingTerm;
  notes: string | null;
  updatedBy: string | null;
  updatedAt: string | null;
}

/** Vertragslaufzeit: Jahresvertrag (mit Mengenstaffel) oder monatlich kündbar. */
export type BillingTerm = 'annual' | 'monthly';

export interface TenantBillingConfigInput {
  billable?: boolean;
  committedSeats?: number | null;
  discountBp?: number;
  flatPriceCents?: number | null;
  billingTerm?: BillingTerm;
  notes?: string | null;
}

export interface TenantBillingBreakdown {
  tenantSlug: string;
  tenantName: string;
  plan: string;
  status: string;
  brand: string;
  customer: { id: string; companyName: string; displayName: string | null } | null;
  periodKey: string;
  periodStatus: 'open' | 'closed';
  priceList: { id: string; name: string } | null;
  config: TenantBillingConfig;
  seats: { licenseSeats: number; activeUsers: number | null; committedSeats: number | null; billable: number };
  activeModules: string[];
  revenue: { lines: BillingLine[]; netCents: number };
  usage: UsageDetail[];
  usageSource: 'frozen' | 'live' | 'federated';
  cost: {
    lines: BillingLine[];
    costCents: number;
    source: 'model' | 'opencost';
    modelCents: number;
    opencostCents: number | null;
    basis: { cpu: 'usage' | 'footprint'; ram: 'usage' | 'footprint'; storage: 'usage' | 'footprint'; loadbalancers: 'usage' | 'footprint' };
  };
  marginCents: number;
  marginPercent: number | null;
  warnings: string[];
}

export interface BillingOverviewRow {
  tenantSlug: string;
  tenantName: string;
  plan: string;
  status: string;
  customer: { id: string; companyName: string } | null;
  billable: boolean;
  billableSeats: number;
  revenueCents: number;
  costCents: number;
  costSource: 'model' | 'opencost';
  marginCents: number;
  marginPercent: number | null;
  warnings: string[];
}

export interface BillingOverview {
  periodKey: string;
  periodStatus: 'open' | 'closed';
  priceLists: Array<{ brand: string; id: string | null; name: string | null }>;
  totals: {
    tenants: number;
    billableTenants: number;
    revenueCents: number;
    costCents: number;
    marginCents: number;
    marginPercent: number | null;
    seats: number;
  };
  rows: BillingOverviewRow[];
  warnings: string[];
}

export interface BillingPeriod {
  key: string;
  periodStart: string;
  periodEnd: string;
  status: 'open' | 'closed';
  closedAt: string | null;
  closedBy: string | null;
  current: boolean;
}

export interface PriceListItem {
  id: string;
  code: string;
  side: BillingSide;
  category: string;
  plan: string | null;
  metric: string | null;
  unit: string;
  unitPriceCents: number;
  includedQuantity: number | null;
  minQuantity: number | null;
  description: string | null;
  sortOrder: number;
}

export type PriceListItemInput = Omit<PriceListItem, 'id'>;

export interface PriceListSummary {
  id: string;
  name: string;
  brand: string;
  currency: string;
  taxRateBp: number;
  status: string; // draft | active | archived
  validFrom: string;
  itemCount: number;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface PriceListDetail extends PriceListSummary {
  items: PriceListItem[];
}

export interface StatementSummary {
  id: string;
  customer: { id: string; companyName: string; displayName: string | null; vatId: string | null; country: string | null; billingEmail: string | null };
  periodKey: string;
  priceList: { id: string; name: string } | null;
  currency: string;
  status: string; // preview | draft | finalized | exported
  netCents: number;
  taxRateBp: number;
  taxCents: number;
  grossCents: number;
  costCents: number;
  marginCents: number;
  reverseCharge: boolean;
  notes: string | null;
  externalRef: string | null;
  /** Übergabe an Intermal (ADR 0014); null = noch nicht übergeben. */
  erpHandoffAt: string | null;
  tenantCount: number;
  lineCount: number;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  finalizedAt: string | null;
  finalizedBy: string | null;
}

export interface StatementLine extends BillingLine {
  id: string | null;
  tenantSlug: string;
  sortOrder: number;
}

export interface StatementDetail extends StatementSummary {
  persisted: boolean;
  lines: StatementLine[];
}

export interface BillingRunResult {
  periodKey: string;
  created: number;
  updated: number;
  skipped: number;
  statements: StatementSummary[];
}

export async function getBillingOverview(period?: string): Promise<BillingOverview> {
  const { data } = await api.get<BillingOverview>('/billing/overview', { params: { period } });
  return data;
}

export async function getTenantBilling(slug: string, period?: string): Promise<TenantBillingBreakdown> {
  const { data } = await api.get<TenantBillingBreakdown>(`/billing/tenants/${slug}`, { params: { period } });
  return data;
}

export async function getTenantBillingConfig(slug: string): Promise<TenantBillingConfig> {
  const { data } = await api.get<TenantBillingConfig>(`/billing/tenants/${slug}/config`);
  return data;
}

export async function updateTenantBillingConfig(slug: string, input: TenantBillingConfigInput): Promise<TenantBillingConfig> {
  const { data } = await api.put<TenantBillingConfig>(`/billing/tenants/${slug}/config`, input);
  return data;
}

export async function previewCustomerStatement(customerId: string, period?: string): Promise<StatementDetail> {
  const { data } = await api.get<StatementDetail>(`/billing/customers/${customerId}/preview`, { params: { period } });
  return data;
}

export async function listPriceLists(): Promise<PriceListSummary[]> {
  const { data } = await api.get<PriceListSummary[]>('/billing/price-lists');
  return data;
}

export async function getPriceList(id: string): Promise<PriceListDetail> {
  const { data } = await api.get<PriceListDetail>(`/billing/price-lists/${id}`);
  return data;
}

export async function createPriceList(input: { name: string; brand?: string; taxRateBp?: number; fromSeed?: boolean; copyFromId?: string | null }): Promise<PriceListDetail> {
  const { data } = await api.post<PriceListDetail>('/billing/price-lists', input);
  return data;
}

export async function updatePriceList(id: string, input: { name?: string; taxRateBp?: number; items?: PriceListItemInput[] }): Promise<PriceListDetail> {
  const { data } = await api.put<PriceListDetail>(`/billing/price-lists/${id}`, input);
  return data;
}

export async function activatePriceList(id: string): Promise<PriceListDetail> {
  const { data } = await api.post<PriceListDetail>(`/billing/price-lists/${id}/activate`);
  return data;
}

export async function archivePriceList(id: string): Promise<PriceListDetail> {
  const { data } = await api.post<PriceListDetail>(`/billing/price-lists/${id}/archive`);
  return data;
}

export async function listBillingPeriods(): Promise<{ current: string; periods: BillingPeriod[] }> {
  const { data } = await api.get<{ current: string; periods: BillingPeriod[] }>('/billing/periods');
  return data;
}

export async function closeBillingPeriod(key: string): Promise<{ period: BillingPeriod; tenants: number; metrics: number }> {
  const { data } = await api.post<{ period: BillingPeriod; tenants: number; metrics: number }>(`/billing/periods/${key}/close`);
  return data;
}

export async function runBillingPeriod(period: string): Promise<BillingRunResult> {
  const { data } = await api.post<BillingRunResult>('/billing/runs', { period });
  return data;
}

export async function listStatements(filter: { period?: string; customerId?: string; status?: string } = {}): Promise<StatementSummary[]> {
  const { data } = await api.get<StatementSummary[]>('/billing/statements', { params: filter });
  return data;
}

export async function getStatement(id: string): Promise<StatementDetail> {
  const { data } = await api.get<StatementDetail>(`/billing/statements/${id}`);
  return data;
}

export async function finalizeStatement(id: string): Promise<StatementDetail> {
  const { data } = await api.post<StatementDetail>(`/billing/statements/${id}/finalize`);
  return data;
}

export async function handoffStatement(id: string): Promise<StatementDetail> {
  const { data } = await api.post<StatementDetail>(`/billing/statements/${id}/erp-handoff`);
  return data;
}

export async function updateStatement(id: string, input: { notes?: string | null; externalRef?: string | null; status?: 'exported' | 'draft' }): Promise<StatementDetail> {
  const { data } = await api.patch<StatementDetail>(`/billing/statements/${id}`, input);
  return data;
}

export async function deleteStatement(id: string): Promise<void> {
  await api.delete(`/billing/statements/${id}`);
}

/** Lädt eine CSV über die authentifizierte API und öffnet den Download. */
export async function downloadCsv(path: string, filename: string): Promise<void> {
  const { data } = await api.get<Blob>(path, { responseType: 'blob' });
  const url = URL.createObjectURL(data);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// --- /api/platform-credentials — externe Zugangsdaten (Seite „Zugangsdaten“).
// Spiegelt `CredentialView` aus platform-admin/src/services/platform-credentials.service.ts.
// Geheime Felder tragen nie einen Wert, nur `set`.

export type PlatformCredentialStatus = 'complete' | 'partial' | 'missing' | 'unknown';

export interface PlatformCredentialField {
  key: string;
  label: string;
  secret: boolean;
  required: boolean;
  placeholder?: string;
  hint?: string;
  /** Nur fuer nicht geheime Felder gefuellt. */
  value: string | null;
  set: boolean;
}

export interface PlatformCredential {
  id: string;
  label: string;
  description: string;
  openbaoPath: string;
  consumers: string[];
  effect: string;
  docs: string | null;
  verifiable: boolean;
  status: PlatformCredentialStatus;
  error: string | null;
  fields: PlatformCredentialField[];
  updatedAt: string | null;
  updatedBy: string | null;
}

export interface PlatformCredentialVerifyResult {
  ok: boolean;
  message: string;
}

export async function listPlatformCredentials(): Promise<PlatformCredential[]> {
  const { data } = await api.get<PlatformCredential[]>('/platform-credentials');
  return data;
}

export async function getPlatformCredential(id: string): Promise<PlatformCredential> {
  const { data } = await api.get<PlatformCredential>(`/platform-credentials/${encodeURIComponent(id)}`);
  return data;
}

/** Schreibt nur die uebergebenen Felder; leerer String loescht ein Feld. */
export async function setPlatformCredential(id: string, values: Record<string, string>): Promise<PlatformCredential> {
  const { data } = await api.put<PlatformCredential>(`/platform-credentials/${encodeURIComponent(id)}`, values);
  return data;
}

export async function clearPlatformCredential(id: string): Promise<PlatformCredential> {
  const { data } = await api.delete<PlatformCredential>(`/platform-credentials/${encodeURIComponent(id)}`);
  return data;
}

export async function verifyPlatformCredential(id: string): Promise<PlatformCredentialVerifyResult> {
  const { data } = await api.post<PlatformCredentialVerifyResult>(`/platform-credentials/${encodeURIComponent(id)}/verify`);
  return data;
}

// ===========================================================================
// KI-Anbieter-Modus je Mandant (ADR 0021, DPA-015) —
// /api/tenants/:slug/ai-settings (tenant-ai-settings.routes.ts).
// storedMode = Entscheidung des Operators, providerMode = wirksam
// (allow_remote nur mit Deployment-Freigabe PLATFORM_ADMIN_AI_REMOTE_ALLOWED).
// ===========================================================================

export type AiProviderMode = 'local_only' | 'allow_remote';

export interface TenantAiSettings {
  slug: string;
  providerMode: AiProviderMode;
  storedMode: AiProviderMode;
  remoteAllowed: boolean;
  /** true = keine gespeicherte Zeile (Vorgabe local_only). */
  isDefault: boolean;
  reason: string | null;
  updatedBy: string | null;
  updatedByEmail: string | null;
  updatedAt: string | null;
}

export interface TenantAiSettingsInput {
  providerMode: AiProviderMode;
  /** Pflicht (mind. 3 Zeichen) fuer allow_remote, max. 500 Zeichen. */
  reason: string;
}

export async function getTenantAiSettings(slug: string): Promise<TenantAiSettings> {
  const { data } = await api.get<TenantAiSettings>(`/tenants/${encodeURIComponent(slug)}/ai-settings`);
  return data;
}

export async function updateTenantAiSettings(slug: string, input: TenantAiSettingsInput): Promise<TenantAiSettings> {
  const { data } = await api.put<TenantAiSettings>(`/tenants/${encodeURIComponent(slug)}/ai-settings`, input);
  return data;
}
